import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { NearConnector } from '@hot-labs/near-connect';
import type { NearV1Asset, NearV1Config, NearV1LifecycleQuote, NearV1Market, NearV1Network, NearV1Operation, NearV1PoolQuote, NearV1PoolRequest, NearV1Portfolio, NearV1Preparation, NearV1PrepareRequest, NearV1Quote, NearV1Session } from '../../../../packages/sdk/src/near';
import { NearV1Client } from '../../../../packages/sdk/src/near';
import { assertNearPoolPreparation, assertNearPoolQuote, assertNearPreparation, assertNearQuote, lifecycleActions, poolActions } from './near-lifecycle';
import type { NearV1Crank, NearV1Progress } from '../../../../packages/sdk/src/near';
import { assertNearCrank, sameCrankAction } from './near-crank';

type ConnectedWallet = Awaited<ReturnType<NearConnector['wallet']>>;
export type NearAction = Parameters<NearV1Client['prepare']>[0]['action'];
type PreparationInput = Omit<NearV1PrepareRequest, 'accountId' | 'network'>;
type PoolInput = Omit<NearV1PoolRequest, 'accountId' | 'network'>;
type NearSubmitResult = { status: 'registered'; hash: string; operation: NearV1Operation } | { status: 'unknown'; hash: string | null; issue: string };
type NearWalletState = {
  config: NearV1Config | null;
  configError: string;
  loading: boolean;
  accountId: string;
  session: NearV1Session | null;
  busy: boolean;
  error: string;
  connect: () => Promise<void>;
  signIn: () => Promise<void>;
  disconnect: () => Promise<void>;
  refresh: () => Promise<void>;
  quote: (action: 'buy' | 'sell', amountRaw: string, launchId?: string, slippageBps?:number) => Promise<NearV1Quote>;
  assets: () => Promise<NearV1Asset[]>;
  markets: () => Promise<NearV1Market[]>;
  portfolio: () => Promise<NearV1Portfolio>;
  lifecycleQuote: (input: PreparationInput) => Promise<NearV1LifecycleQuote>;
  poolQuote: (input: PoolInput) => Promise<NearV1PoolQuote>;
  preparePool: (input: PoolInput, quote: NearV1PoolQuote) => Promise<NearV1Preparation>;
  prepare: (input: PreparationInput, quote?: NearV1LifecycleQuote) => Promise<NearV1Preparation>;
  submit: (reviewed: NearV1Preparation) => Promise<NearSubmitResult>;
  operations: () => Promise<NearV1Operation[]>;
  operation: (id: string) => Promise<NearV1Operation>;
  pendingHash: (operation: NearV1Operation) => string | null;
  recoverHash: (operation: NearV1Operation) => Promise<NearV1Operation>;
  progress: (id: string) => Promise<NearV1Progress>;
  prepareCrank: (id: string) => Promise<NearV1Crank>;
  submitCrank: (crank: NearV1Crank) => Promise<string>;
  pendingCrankHash: (crank: NearV1Crank) => string | null;
  recoverCrankHash: (crank: NearV1Crank) => Promise<NearV1Crank>;
};

const NearContext = createContext<NearWalletState | null>(null);
const client = new NearV1Client();
const message = (error: unknown) => error instanceof Error ? error.message : 'The NEAR wallet request failed.';
const hashKey = (id: string) => `overture:near:operation-hash:${id}`;
type SavedHash = { accountId: string; network: NearV1Network; deploymentId: string; hash: string };
const readSavedHash = (id: string): SavedHash | null => {
  try {
    const value = localStorage.getItem(hashKey(id));
    if (!value) return null;
    const parsed = JSON.parse(value) as SavedHash;
    return parsed?.accountId && parsed.network && parsed.deploymentId && /^[A-Za-z0-9_-]{32,128}$/.test(parsed.hash) ? parsed : null;
  } catch { return null; }
};

export function useNearWallet() {
  const value = useContext(NearContext);
  if (!value) throw new Error('NEAR wallet provider missing');
  return value;
}

export function NearWalletProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<NearV1Config | null>(null);
  const [configError, setConfigError] = useState('');
  const [loading, setLoading] = useState(true);
  const [accountId, setAccountId] = useState('');
  const [session, setSession] = useState<NearV1Session | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const connector = useRef<NearConnector | null>(null);
  const wallet = useRef<ConnectedWallet | null>(null);
  const account = useRef('');
  const generation = useRef(0);
  const configScope = useRef('');

  const setCurrentConfig = (next: NearV1Config) => {
    const scope = JSON.stringify([next.network, next.deploymentId, next.localFixture, next.contracts]);
    if (configScope.current && configScope.current !== scope) generation.current++;
    configScope.current = scope;
    setConfig(next);
  };

  const setCurrentAccount = (next: string) => {
    if (account.current !== next) generation.current++;
    account.current = next;
    setAccountId(next);
    setSession(existing => existing?.accountId === next ? existing : null);
  };

  const refresh = async () => {
    const epoch = generation.current;
    try {
      const [nextConfig, nextSession] = await Promise.all([client.config(), client.session()]);
      if (generation.current !== epoch) return;
      setCurrentConfig(nextConfig);
      setConfigError(''); setError('');
      setSession(nextSession?.network === nextConfig.network ? nextSession : null);
      if (connector.current && nextConfig.network !== connector.current.network) {
        connector.current = null;
        wallet.current = null;
        setCurrentAccount('');
      }
    } catch (e) { setConfigError(message(e)); setError(message(e)); }
  };

  useEffect(() => {
    let active = true;
    Promise.all([client.config(), client.session()]).then(([nextConfig, nextSession]) => {
      if (!active) return;
      setCurrentConfig(nextConfig);
      setSession(nextSession?.network === nextConfig.network ? nextSession : null);
    }).catch(e => { if (active) setConfigError(message(e)); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; generation.current++; };
  }, []);

  const getConnector = async (network: NearV1Network) => {
    if (connector.current?.network === network) return connector.current;
    const { NearConnector } = await import('@hot-labs/near-connect');
    const next = new NearConnector({
      network,
      autoConnect: false,
      features: { signMessage: true, signInWithoutAddKey: true, [network]: true },
      footerBranding: null,
    });
    await next.whenManifestLoaded;
    next.on('wallet:signOut', () => { wallet.current = null; setCurrentAccount(''); setSession(null); });
    next.on('wallet:signIn', ({ accounts }) => {
      const nextAccount = accounts[0]?.accountId || '';
      if (nextAccount) setCurrentAccount(nextAccount);
    });
    connector.current = next;
    return next;
  };

  const connect = async () => {
    if (!config?.network || !config.capabilities.authentication.available) {
      setError(config?.capabilities.authentication.reason || 'NEAR network configuration is unavailable.');
      return;
    }
    setBusy(true); setError('');
    try {
      const next = await getConnector(config.network);
      const selected = await next.connect();
      const accounts = await selected.getAccounts({ network: config.network });
      if (next.network !== config.network || !accounts[0]?.accountId) throw new Error('The wallet did not return an account on the configured NEAR network.');
      wallet.current = selected;
      setCurrentAccount(accounts[0].accountId);
      if (session && (session.accountId !== accounts[0].accountId || session.network !== config.network)) {
        await client.logout().catch(() => {});
        setSession(null);
      }
    } catch (e) { setError(message(e)); }
    finally { setBusy(false); }
  };

  const assertWallet = async (selected: ConnectedWallet, network: NearV1Network, expected: string, epoch: number) => {
    const accounts = await selected.getAccounts({ network });
    if (generation.current !== epoch || connector.current?.network !== network || wallet.current !== selected || !accounts.some(value => value.accountId === expected)) {
      throw new Error('The NEAR account or network changed. Reconnect and review again.');
    }
  };

  const signIn = async () => {
    if (!config?.network || !config.capabilities.authentication.available || !account.current || !wallet.current) {
      setError('Connect a supported NEAR wallet on the configured network first.');
      return;
    }
    setBusy(true); setError('');
    const selected = wallet.current, selectedAccount = account.current, network = config.network, epoch = generation.current;
    try {
      await assertWallet(selected, network, selectedAccount, epoch);
      const challenge = await client.challenge(selectedAccount, network);
      await assertWallet(selected, network, selectedAccount, epoch);
      if (challenge.accountId !== selectedAccount || challenge.network !== network || challenge.expiresAt <= Date.now()) throw new Error('The NEAR sign-in challenge is stale. Retry sign-in.');
      const nonce = Uint8Array.from(atob(challenge.nonce), character => character.charCodeAt(0));
      if (nonce.length !== 32) throw new Error('The NEAR sign-in challenge is invalid.');
      const signed = await selected.signMessage({ message: challenge.message, recipient: challenge.recipient, nonce, network, signerId: selectedAccount });
      await assertWallet(selected, network, selectedAccount, epoch);
      if (signed.accountId !== selectedAccount) throw new Error('The wallet signed for another NEAR account.');
      const next = await client.verify(challenge.id, selectedAccount, signed.publicKey, signed.signature);
      let nextConfig: NearV1Config;
      try {
        nextConfig = await client.config();
        await assertWallet(selected, network, selectedAccount, epoch);
        if (nextConfig.network !== network) throw new Error('The NEAR network changed during sign-in. Reconnect and review again.');
      }
      catch (e) { await client.logout().catch(() => {}); throw e; }
      setCurrentConfig(nextConfig);
      setSession(next);
    } catch (e) { setError(message(e)); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true); setError('');
    generation.current++;
    try {
      if (session) await client.logout();
      if (connector.current) await connector.current.disconnect();
      wallet.current = null;
      setCurrentAccount('');
      setSession(null);
      await refresh();
    } catch (e) { setError(message(e)); }
    finally { setBusy(false); }
  };

  const quote = async (action: 'buy' | 'sell', amountRaw: string, launchId?: string, slippageBps?:number) => {
    const network = config?.network, selected = wallet.current, selectedAccount = account.current;
    if (!network || !selected || !selectedAccount || !session || !config.capabilities.trade.available) {
      throw new Error(config?.capabilities.trade.reason || 'Native trading is unavailable.');
    }
    const epoch = generation.current;
    await assertWallet(selected, network, selectedAccount, epoch);
    const result = await client.quote({ action, accountId: selectedAccount, network, amountRaw, launchId, slippageBps });
    await assertWallet(selected, network, selectedAccount, epoch);
    if (result.localFixture !== config.localFixture || result.action !== action || result.network !== network || result.accountId !== selectedAccount || result.deploymentId !== config.deploymentId || (launchId && result.launchId !== launchId) || result.input.raw !== amountRaw || result.expiresAt <= Date.now()) {
      throw new Error('The NEAR quote does not match this account, deployment or input.');
    }
    return result;
  };

  const assets = async () => {
    const next = await client.assets();
    if (next.some(value => value.localFixture !== config?.localFixture)) throw new Error('The source asset environment changed. Refresh status.');
    return next;
  };

  const markets = async () => {
    const next = await client.markets();
    if (next.some(value => value.localFixture !== config?.localFixture)) throw new Error('The native market environment changed. Refresh status.');
    return next;
  };

  const portfolio = async () => {
    const network = config?.network, selected = wallet.current, selectedAccount = account.current;
    if (!network || !selected || !selectedAccount || !session) throw new Error('Sign in with a NEAR wallet to read your portfolio.');
    const epoch = generation.current;
    await assertWallet(selected, network, selectedAccount, epoch);
    const result = await client.portfolio();
    await assertWallet(selected, network, selectedAccount, epoch);
    if (result.network !== network || result.accountId !== selectedAccount || result.localFixture !== config.localFixture) throw new Error('The native portfolio belongs to another account or environment.');
    return result;
  };

  const lifecycleQuote = async (input: PreparationInput) => {
    const network = config?.network, selected = wallet.current, selectedAccount = account.current;
    const capability = input.action in lifecycleActions ? config?.capabilities[input.action as keyof typeof lifecycleActions] : undefined;
    if (!network || !selected || !selectedAccount || !session || !capability?.available) throw new Error(capability?.reason || 'This native lifecycle route is unavailable.');
    const epoch = generation.current;
    await assertWallet(selected, network, selectedAccount, epoch);
    const request = { ...input, accountId: selectedAccount, network };
    const result = await client.lifecycleQuote(request);
    await assertWallet(selected, network, selectedAccount, epoch);
    assertNearQuote(config, result, request);
    return result;
  };

  const poolCapability = (action: NearV1PoolRequest['action']) => action.startsWith('swap_') ? 'poolSwap' : action.startsWith('claim_pool_') ? 'poolClaims' : 'poolLiquidity';
  const poolQuote = async (input: PoolInput) => {
    const network = config?.network, selected = wallet.current, selectedAccount = account.current;
    const capability = config?.capabilities[poolCapability(input.action)];
    if (!network || !selected || !selectedAccount || !session || !capability?.available || !(input.action in poolActions)) throw new Error(capability?.reason || 'This native pool route is unavailable.');
    const epoch = generation.current;
    await assertWallet(selected, network, selectedAccount, epoch);
    const request = { ...input, accountId: selectedAccount, network };
    const result = await client.poolQuote(request);
    await assertWallet(selected, network, selectedAccount, epoch);
    assertNearPoolQuote(config, result, request);
    return result;
  };

  const preparePool = async (input: PoolInput, quote: NearV1PoolQuote) => {
    const network = config?.network, selected = wallet.current, selectedAccount = account.current;
    const capability = config?.capabilities[poolCapability(input.action)];
    if (!network || !selected || !selectedAccount || !session || !capability?.available || !config.deploymentId || !(input.action in poolActions)) throw new Error(capability?.reason || 'This native pool action is unavailable.');
    const epoch = generation.current;
    await assertWallet(selected, network, selectedAccount, epoch);
    const currentSession = await client.session();
    if (currentSession?.accountId !== selectedAccount || currentSession.network !== network || currentSession.expiresAt <= Date.now()) throw new Error('Your NEAR sign-in expired. Sign in again to review a pool action.');
    const request = { ...input, accountId: selectedAccount, network };
    assertNearPoolQuote(config, quote, request);
    const reviewed = await client.preparePool(request);
    await assertWallet(selected, network, selectedAccount, epoch);
    assertNearPoolPreparation(config, reviewed, request, quote);
    return reviewed;
  };

  const prepare = async (input: PreparationInput, quote?: NearV1LifecycleQuote) => {
    const network = config?.network, selected = wallet.current, selectedAccount = account.current;
    const action = ({acquire:'buyer_acquire',graduate:'fund_graduation',rewards:'claim_rewards',redeem:'request_redemption'} as Record<string,string>)[input.action] || input.action;
    const capability = config?.capabilities[action === 'buy' || action === 'sell' ? 'trade' : action as keyof NearV1Config['capabilities']];
    if (!network || !selected || !selectedAccount || !session || !capability?.available || !config.deploymentId) {
      throw new Error(capability?.reason || 'This native NEAR action is unavailable.');
    }
    const epoch = generation.current;
    await assertWallet(selected, network, selectedAccount, epoch);
    const currentSession = await client.session();
    if (currentSession?.accountId !== selectedAccount || currentSession.network !== network || currentSession.expiresAt <= Date.now()) {
      throw new Error('Your NEAR sign-in expired. Sign in again to review an action.');
    }
    const request = { ...input, accountId: selectedAccount, network };
    if (quote) assertNearQuote(config, quote, request);
    const reviewed = await client.prepare(request);
    await assertWallet(selected, network, selectedAccount, epoch);
    assertNearPreparation(config, reviewed, request, quote);
    return reviewed;
  };

  const submit = async (reviewed: NearV1Preparation): Promise<NearSubmitResult> => {
    const network = config?.network, selected = wallet.current, selectedAccount = account.current;
    if (!network || !selected || !selectedAccount || !session || reviewed.network !== network || reviewed.signerId !== selectedAccount || reviewed.deploymentId !== config?.deploymentId || reviewed.expiresAt <= Date.now()) {
      throw new Error('The NEAR account, network, deployment or review expiry changed. Prepare again.');
    }
    assertNearPreparation(config, reviewed);
    const epoch = generation.current;
    await assertWallet(selected, network, selectedAccount, epoch);
    const revalidated = await client.revalidateOperation(reviewed.id);
    await assertWallet(selected, network, selectedAccount, epoch);
    if (JSON.stringify(revalidated) !== JSON.stringify(reviewed)) throw new Error('The NEAR preparation changed. Review a fresh quote.');
    let dispatched: NearV1Operation;
    try { dispatched = await client.dispatchOperation(reviewed.id); }
    catch (e) { return { status: 'unknown', hash: null, issue: `The wallet attempt could not be safely confirmed in the journal. Check activity before any new action. ${message(e)}` }; }
    if (dispatched.state !== 'broadcast_unknown' || dispatched.accountId !== selectedAccount || dispatched.network !== network || dispatched.deploymentId !== config.deploymentId) {
      return { status: 'unknown', hash: null, issue: 'The native operation journal returned an unexpected state. Check activity before any new action.' };
    }
    try { await assertWallet(selected, network, selectedAccount, epoch); }
    catch (e) { return { status: 'unknown', hash: null, issue: `The NEAR account or network changed before wallet approval. No wallet call was made; check the operation journal before preparing again. ${message(e)}` }; }
    if (reviewed.expiresAt <= Date.now()) return { status: 'unknown', hash: null, issue: 'The review expired before wallet approval. No wallet call was made; check the operation journal before preparing again.' };
    let hash: string | null = null;
    try {
      const result = await selected.signAndSendTransaction({
        network,
        signerId: selectedAccount,
        receiverId: reviewed.receiverId,
        actions: reviewed.actions.map(action => ({ type: 'FunctionCall' as const, params: { methodName: action.methodName, args: action.args, gas: action.gas, deposit: action.depositYocto } })),
      });
      hash = result.transaction?.hash || null;
    } catch (e) {
      return { status: 'unknown', hash: null, issue: `The wallet did not return a transaction hash. Check wallet and chain activity before preparing another action. ${message(e)}` };
    }
    if (!hash) return { status: 'unknown', hash: null, issue: 'The wallet returned without a transaction hash. Check wallet and chain activity before preparing another action.' };
    let saved = false;
    try { localStorage.setItem(hashKey(reviewed.id), JSON.stringify({ accountId: selectedAccount, network, deploymentId: config.deploymentId, hash } satisfies SavedHash)); saved = true; } catch { /* The journal still gets its chance to record the hash. */ }
    try {
      const operation = await client.registerOperation(reviewed.id, hash);
      try { localStorage.removeItem(hashKey(reviewed.id)); } catch { /* An old recovery hint is harmless. */ }
      return { status: 'registered', hash, operation };
    } catch (e) {
      return { status: 'unknown', hash, issue: `The wallet returned a hash, but the operation journal could not record it. ${saved ? 'The hash is saved in this browser for recovery.' : 'Copy this hash now; this browser could not save it.'} Do not send the transaction again. ${message(e)}` };
    }
  };

  const operations = () => client.operations();
  const progress = (id: string) => client.progress(id);
  const prepareCrank = async (id: string) => {
    const selected = wallet.current, selectedAccount = account.current, network = config?.network;
    if (!selected || !network || !config || session?.accountId !== selectedAccount) throw new Error('Verify the original NEAR account first.');
    const epoch = generation.current;
    await assertWallet(selected, network, selectedAccount, epoch);
    const next = await client.progress(id);
    if (next.latestCrank?.state === 'prepared' && next.latestCrank.signerId === selectedAccount && next.latestCrank.expiresAt > Date.now()) {
      await assertWallet(selected, network, selectedAccount, epoch);
      assertNearCrank(config, next.latestCrank, selectedAccount, id, next);
      return next.latestCrank;
    }
    const prepared = await client.prepareCrank(id);
    await assertWallet(selected, network, selectedAccount, epoch);
    assertNearCrank(config, prepared, selectedAccount, id, next);
    if (!next.nextCrank || !sameCrankAction(next.nextCrank, prepared.action)) throw new Error('The continuation changed. Refresh its progress.');
    return prepared;
  };
  const pendingCrankHash = (crank: NearV1Crank) => {
    const saved = readSavedHash(crank.id);
    return saved && account.current === crank.signerId && session?.accountId === crank.signerId &&
      saved.accountId === crank.signerId && saved.network === crank.network &&
      saved.deploymentId === config?.deploymentId ? saved.hash : null;
  };
  const recoverCrankHash = async (crank: NearV1Crank) => {
    const hash = pendingCrankHash(crank);
    if (!hash || account.current !== crank.signerId || session?.accountId !== crank.signerId ||
        session.network !== crank.network || config?.network !== crank.network) throw new Error('Verify the original account to attach this continuation hash.');
    const result = await client.registerCrank(crank.id, hash);
    try { localStorage.removeItem(hashKey(crank.id)); } catch { /* Journal is authoritative. */ }
    return result;
  };
  const submitCrank = async (reviewed: NearV1Crank): Promise<string> => {
    const selected = wallet.current, selectedAccount = account.current, network = config?.network;
    if (!selected || !network || !config?.deploymentId || session?.accountId !== selectedAccount) throw new Error('Verify the original NEAR account first.');
    assertNearCrank(config, reviewed, selectedAccount, reviewed.parentOperationId);
    const epoch = generation.current;
    await assertWallet(selected, network, selectedAccount, epoch);
    const progress = await client.progress(reviewed.parentOperationId);
    await assertWallet(selected, network, selectedAccount, epoch);
    assertNearCrank(config, reviewed, selectedAccount, reviewed.parentOperationId, progress);
    if (!progress.nextCrank || !sameCrankAction(progress.nextCrank, reviewed.action)) throw new Error('The continuation stage changed. Refresh its progress.');
    const current = await client.crank(reviewed.id);
    if (JSON.stringify(current) !== JSON.stringify(reviewed)) throw new Error('The continuation changed. Review it again.');
    await assertWallet(selected, network, selectedAccount, epoch);
    // Once dispatch is attempted, any error requires journal recovery before another wallet call.
    const dispatched = await client.dispatchCrank(reviewed.id);
    if (dispatched.state !== 'broadcast_unknown' || dispatched.signerId !== selectedAccount ||
        dispatched.network !== network || !sameCrankAction(dispatched.action, reviewed.action)) throw new Error('Continuation dispatch is unresolved. Check Activity before another attempt.');
    await assertWallet(selected, network, selectedAccount, epoch);
    if (reviewed.expiresAt <= Date.now()) throw new Error('The continuation review expired before wallet approval. No wallet call was made; check Activity before another attempt.');
    let hash: string | null = null;
    try {
      const result = await selected.signAndSendTransaction({ network, signerId: selectedAccount,
        receiverId: reviewed.action.receiverId, actions: [{ type: 'FunctionCall', params: {
          methodName: reviewed.action.methodName, args: reviewed.action.args,
          gas: reviewed.action.gas, deposit: reviewed.action.depositYocto,
        } }] });
      hash = result.transaction?.hash || null;
    } catch { throw new Error('The continuation wallet outcome is unknown. Check Activity and wallet history before another attempt.'); }
    if (!hash) throw new Error('No continuation hash was returned. Check Activity and wallet history before another attempt.');
    let saved = false;
    try { localStorage.setItem(hashKey(reviewed.id), JSON.stringify({ accountId: selectedAccount, network, deploymentId: config.deploymentId, hash } satisfies SavedHash)); saved = true; } catch { /* Register remains available. */ }
    try {
      await client.registerCrank(reviewed.id, hash);
      try { localStorage.removeItem(hashKey(reviewed.id)); } catch { /* Journal is authoritative. */ }
      return hash;
    } catch { throw new Error(`Continuation hash ${hash} could not be attached. ${saved ? 'Saved in this browser; use Attach saved continuation hash in Activity.' : 'Copy this hash now.'} Do not send it again.`); }
  };
  const operation = (id: string) => client.operation(id);
  const pendingHash = (entry: NearV1Operation) => {
    const saved = readSavedHash(entry.id);
    return saved && saved.accountId === entry.accountId && saved.network === entry.network && saved.deploymentId === entry.deploymentId ? saved.hash : null;
  };
  const recoverHash = async (entry: NearV1Operation) => {
    const hash = pendingHash(entry);
    if (!hash || !config?.network || !config.deploymentId || !session || session.accountId !== entry.accountId || session.network !== entry.network || account.current !== entry.accountId) {
      throw new Error('Connect and verify the original NEAR account to recover this transaction hash.');
    }
    const latest = await client.operation(entry.id);
    if (latest.accountId !== entry.accountId || latest.network !== entry.network || latest.deploymentId !== entry.deploymentId || latest.state !== 'broadcast_unknown') {
      throw new Error('The operation changed. Refresh its status before recovery.');
    }
    const registered = await client.registerOperation(entry.id, hash);
    try { localStorage.removeItem(hashKey(entry.id)); } catch { /* The journal is authoritative. */ }
    return registered;
  };

  return <NearContext.Provider value={{ config, configError, loading, accountId, session, busy, error, connect, signIn, disconnect, refresh, quote, assets, markets, portfolio, lifecycleQuote, poolQuote, preparePool, prepare, submit, operations, operation, pendingHash, recoverHash, progress, prepareCrank, submitCrank, pendingCrankHash, recoverCrankHash }}>
    {children}
  </NearContext.Provider>;
}
